/**
 * @file        eventemitter-manager - a utility class to all us to manage EventEmitters' events with
 *                                     less specificity than (eventName,listener) but more specificity
 *                                     than "all of them".
 * @author      Wes Garland, wes@distributive.network
 * @date        nov 2022
 * 
 * @example
 * const et = new EventThing();
 * et.on('message', () => doit());
 * ...leak;
 *
 * becomes
 *
 * const abc = new EventThing();
 * const et = new EventEmitterManager(abc);
 * et.on('message', () => doit());
 * ...
 * et.off('message');
 *
 * and then eventually we can et.removeAllListeners() which will affect et but not events registered
 * directly on abc.
 */

/**
 * An EventEmitter manager. Does not care what kind of implementation the EventEmitter is, so long as it
 * uses the standard Node events API. This class works by jimmying the underlying event emitter onto
 * object's prototype chain, intercepting on/off (etc) methods to keep a registry of which of the
 * underlying event handlers were registered via this manager. We then add the sematic to the standard
 * API that is ~ "if we were passed undefined then it means all" in certain contexts.
 * 
 * @constructor
 *
 * @returns an instance of whatever underlying is an instance of
 */
exports.EventEmitterManager = function EventEmitterManager(underlying)
{
  const registry = {};

  /** Same as events::on, but managed through this class */
  this.on = function EventEmitterManager$on(eventName, listener)
  {
    addToRegistry(eventName, listener);
    return underlying.on(eventName, listener);
  }
  this.addEventListener = this.on;
  this.addListener = this.on;

  /** Same as events::prependListener, but managed through this class */
  this.prependListener = function EventEmitterManager$prependListener(eventName, listener)
  {
    addToRegistry(eventName, listener);
    return underlying.prependListener(eventName, listener);
  }

  /** Same as events::once, but managed through this class, and implemented via this.on() */
  this.once = function EventEmitterManager$once(eventName, listener)
  {
    const that = this;
    var fired = false;
    
    function listenerWrapper()
    {
      if (!fired)
        listener.apply(this, arguments);
      removeFromRegistry(eventName, listenerWrapper);
    }

    this.on(eventName, listenerWrapper);
  }

  /** Same as events::once, but managed through this class, and implemented via this.prependListener() */
  this.prependOnceListener = function EventEmitterManager$prependOnceListener(eventName, listener)
  {
    const that = this;
    var fired = false;
    
    function listenerWrapper()
    {
      if (!fired)
        listener.apply(this, arguments);
      removeFromRegistry(eventName, listenerWrapper);
    }

    this.prependListener(eventName, listenerWrapper);
  }

  /** Same as events::off, but managed through this class */
  this.off = function EventEmitterManager$off(eventName, listener)
  {
    removeFromRegistry(eventName, listener);
    underlying.off(eventName, listener);
  }
  this.removeEventListener = this.off;
  this.removeListener = this.off;

  /** 
   * Same API as events::removeAllListeners, except when eventName is undefined, in which case we remove
   * all the event listeners for all event names associated with this event manager. Additionally event
   * listeners which were registered directly on the underlying event emitter (or another instance of
   * this class) are left alone.
   */
  this.removeAllListeners = function EventEmitterManager$removeAllListeners(eventName)
  {
    if (eventName)
    {
      /* Remove all eventName-events registered with us */
      const list = registry[eventName];

      for (let i=0; i < list.length; i++)
        underlying.off(eventName, list[i].listener);
      list.length = 0;
    }
    else
    {
      /* Remove all events registered with us */
      for (let eventName of this.eventNames())
        this.removeAllListeners(eventName);
    }
  }

  /** Return a list of event names managed by this instance */
  this.eventNames = function EventEmitterManager$eventNames(eventName)
  {
    return Object.keys(registry);
  }

  /** Return a list of listeners for this event name managed by this instance */
  this.rawListeners = function EventEmitterManager$rawListeners(eventName)
  {
    return registry[eventName];
  }

  function removeFromRegistry(eventName, listener)
  {
    const list = registry[eventName];

    for (let i=0; i < list.length; i++)
    {
      if (list[i].listener === listener)
      {
        list.splice(i, 1);
        break;
      }
    }
  }

  function addToRegistry(eventName, listener)
  {
    if (!registry.hasOwnProperty(eventName))
      registry[eventName] = [];
    
    registry[eventName].push({eventName, listener});
  }

  Object.setPrototypeOf(this, underlying);
}
